import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime, Schema } from 'effect';
import { Effect, Option, Request, RequestResolver } from 'effect';

import type { ApiInfrastructureErrors } from '../../../data/error/api';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/customers/me';
const HTTP_METHOD = 'get';

export type CustomerAuthPayload = {
  readonly username: string;
};

export type MakeGetCustomerMeDeps<CustomerMeSchema extends Schema.Top, AuthRequirements> = {
  readonly customerMe: CustomerMeSchema;
  readonly customerEmail: (me: CustomerMeSchema['Type']) => string;
  readonly decodeAuthTokenFromCookies: Effect.Effect<
    Option.Option<CustomerAuthPayload>,
    never,
    AuthRequirements
  >;
};

export const makeGetCustomerMe = <CustomerMeSchema extends Schema.Top, AuthRequirements>(
  deps: MakeGetCustomerMeDeps<CustomerMeSchema, AuthRequirements>
) => {
  const GetCustomerMeError = createBackendApiError({
    domain: 'Customer',
    endpoint: 'GetMe',
    path: ENDPOINT,
    identifierField: 'id',
  });

  const getCustomerMeFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({});
  });

  const getCustomerMe = () =>
    getCustomerMeFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.customerMe)(response),
          404: (response) =>
            Effect.fail(new GetCustomerMeError.NotFoundError({ id: 'me', response })),
          orElse: (response) =>
            Effect.fail(new GetCustomerMeError.ApiRequestError({ id: 'me', response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.CustomerGet,
          'Debug',
          `Successfully resolved customer -> email: ${deps.customerEmail(result)}`
        )
      ),
      Effect.withSpan('api.customer.get', {
        kind: 'internal',
      })
    );

  type GetCustomerMeRequest = {
    readonly _tag: 'GetCustomerMeRequest';
    readonly username: string;
  } & Request.Request<
    CustomerMeSchema['Type'],
    | InstanceType<typeof GetCustomerMeError.NotFoundError>
    | InstanceType<typeof GetCustomerMeError.ApiRequestError>
    | ApiInfrastructureErrors,
    | BackendApi
    | AuthToken
    | HttpClient.HttpClient
    | LoggerConfig
    | DateTime.CurrentTimeZone
    | AuthRequirements
  >;

  const GetCustomerMeRequest = Request.tagged<GetCustomerMeRequest>('GetCustomerMeRequest');
  const GetCustomerMeResolver = RequestResolver.fromEffect(
    liftRequest((_request: GetCustomerMeRequest) => getCustomerMe())
  );

  const requestCustomerMe = () =>
    Effect.gen(function* () {
      const jwtPayloadOption = yield* deps.decodeAuthTokenFromCookies;
      const username = Option.match(jwtPayloadOption, {
        onNone: () => 'anonymous',
        onSome: (payload) => payload.username,
      });

      return yield* Effect.request(GetCustomerMeRequest({ username }), GetCustomerMeResolver);
    });

  return { GetCustomerMeError, requestCustomerMe };
};
